// src/admin/pages/BookingDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { EmptyState } from "../ui/EmptyState";

const STATUS_OPTIONS = ["requested", "approved", "active", "completed", "cancelled"];

function daysBetween(startISO, endISO) {
  if (!startISO || !endISO) return null;
  const a = new Date(startISO);
  const b = new Date(endISO);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const ms = b.getTime() - a.getTime();
  const raw = Math.round(ms / (1000 * 60 * 60 * 24));
  return raw === 0 ? 1 : raw;
}

function safeDaysBetween(startISO, endISO) {
  if (!startISO || !endISO) return 0;
  const a = new Date(startISO);
  const b = new Date(endISO);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const ms = b.getTime() - a.getTime();
  const raw = Math.round(ms / (1000 * 60 * 60 * 24));
  return raw <= 0 ? 1 : raw;
}

function toneForStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "requested") return "warning";
  if (s === "approved") return "success";
  if (s === "active") return "info";
  if (s === "completed") return "neutral";
  if (s === "cancelled") return "neutral";
  return "neutral";
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

function formatMoney(n) {
  const value = Number(n || 0);
  return `$${value.toFixed(2)}`;
}

export default function BookingDetail() {
  const { bookingId } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [form, setForm] = useState({
    status: "requested",
    start_date: "",
    end_date: "",
    pickup_location: "",
    concierge: "",
    notes: "",
  });

  const [deliveryForm, setDeliveryForm] = useState({
    service_type: "pickup",
    delivery_address: "",
    free_trip_used: false,
    trips_used: 0,
    outbound_trip_used: false,
    return_trip_used: false,
    outbound_distance_km: "",
    return_distance_km: "",
    distance_km: "",
    included_km: "",
    outbound_billable_km: "",
    return_billable_km: "",
    billable_km: "",
    rate_per_km: "",
    outbound_fee: "",
    return_fee: "",
    delivery_fee: "",
  });

  const bookingDays = useMemo(() => {
    return daysBetween(form.start_date, form.end_date) ?? "—";
  }, [form.start_date, form.end_date]);

  const member = row?.memberships || null;
  const contact = member?.crm_contacts || null;
  const vehicle = row?.vehicles || null;
  const delivery = row?.booking_delivery || null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setDelivery = (k, v) => setDeliveryForm((p) => ({ ...p, [k]: v }));

  const loadBooking = useCallback(async () => {
    const { data, error } = await supabase
      .from("bookings")
      .select(
        `
        id,
        created_at,
        membership_id,
        vehicle_id,
        profile_id,
        created_by,
        start_date,
        end_date,
        status,
        pickup_location,
        dropoff_location,
        notes,
        concierge,
        memberships:membership_id (
          id,
          tier,
          status,
          days_remaining,
          crm_contacts:contact_id (
            id,
            first_name,
            last_name,
            email,
            phone
          )
        ),
        vehicles:vehicle_id (
          id,
          name,
          category
        ),
        booking_delivery (
          booking_id,
          service_type,
          delivery_address,
          free_trip_used,
          trips_used,
          outbound_trip_used,
          return_trip_used,
          outbound_distance_km,
          return_distance_km,
          distance_km,
          included_km,
          outbound_billable_km,
          return_billable_km,
          billable_km,
          rate_per_km,
          outbound_fee,
          return_fee,
          delivery_fee
        )
      `
      )
      .eq("id", bookingId)
      .single();

    if (error) throw error;

    return {
      ...data,
      booking_delivery: Array.isArray(data?.booking_delivery)
        ? data.booking_delivery[0] || null
        : data?.booking_delivery || null,
    };
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr(null);
      setSaveMsg(null);

      try {
        const data = await loadBooking();

        if (!cancelled) {
          setRow(data);
          setForm({
            status: data?.status || "requested",
            start_date: data?.start_date || "",
            end_date: data?.end_date || "",
            pickup_location: data?.pickup_location || "",
            concierge: data?.concierge || "",
            notes: data?.notes || "",
          });

          const d = data?.booking_delivery || null;
          setDeliveryForm({
            service_type: d?.service_type || "pickup",
            delivery_address: d?.delivery_address || "",
            free_trip_used: !!d?.free_trip_used,
            trips_used: d?.trips_used ?? 0,
            outbound_trip_used: !!d?.outbound_trip_used,
            return_trip_used: !!d?.return_trip_used,
            outbound_distance_km: d?.outbound_distance_km ?? "",
            return_distance_km: d?.return_distance_km ?? "",
            distance_km: d?.distance_km ?? "",
            included_km: d?.included_km ?? "",
            outbound_billable_km: d?.outbound_billable_km ?? "",
            return_billable_km: d?.return_billable_km ?? "",
            billable_km: d?.billable_km ?? "",
            rate_per_km: d?.rate_per_km ?? "",
            outbound_fee: d?.outbound_fee ?? "",
            return_fee: d?.return_fee ?? "",
            delivery_fee: d?.delivery_fee ?? "",
          });
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load booking.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [loadBooking]);

  async function onSave() {
    setBusy(true);
    setSaveMsg(null);
    setErr(null);

    try {
      if (!form.start_date || !form.end_date) {
        throw new Error("Start date and end date are required.");
      }

      const newDays = safeDaysBetween(form.start_date, form.end_date);
      if (newDays < 1) throw new Error("Invalid date range.");

      const oldStatus = String(row?.status || "").toLowerCase();
      const newStatus = String(form.status || "").toLowerCase();

      const oldDays = safeDaysBetween(row?.start_date, row?.end_date);

      const wasCompleted = oldStatus === "completed";
      const isCompleted = newStatus === "completed";

      let daysDelta = 0;

      if (!wasCompleted && isCompleted) daysDelta = -newDays;
      if (wasCompleted && !isCompleted) daysDelta = oldDays;
      if (wasCompleted && isCompleted) daysDelta = oldDays - newDays;

      const bookingPayload = {
        status: newStatus,
        start_date: form.start_date,
        end_date: form.end_date,
        pickup_location: form.pickup_location || null,
        concierge: form.concierge || null,
        notes: form.notes || null,
      };

      const { error: bookingErr } = await supabase
        .from("bookings")
        .update(bookingPayload)
        .eq("id", bookingId);

      if (bookingErr) throw bookingErr;

      const hasDeliveryRow = !!delivery;

      const deliveryPayload = {
        service_type: deliveryForm.service_type || "pickup",
        delivery_address:
          deliveryForm.service_type === "delivery"
            ? deliveryForm.delivery_address || null
            : null,
        free_trip_used: !!deliveryForm.free_trip_used,
        trips_used: Number(deliveryForm.trips_used || 0),
        outbound_trip_used: !!deliveryForm.outbound_trip_used,
        return_trip_used: !!deliveryForm.return_trip_used,
        outbound_distance_km:
          deliveryForm.outbound_distance_km === "" ? null : Number(deliveryForm.outbound_distance_km),
        return_distance_km:
          deliveryForm.return_distance_km === "" ? null : Number(deliveryForm.return_distance_km),
        distance_km:
          deliveryForm.distance_km === "" ? null : Number(deliveryForm.distance_km),
        included_km:
          deliveryForm.included_km === "" ? 0 : Number(deliveryForm.included_km),
        outbound_billable_km:
          deliveryForm.outbound_billable_km === "" ? 0 : Number(deliveryForm.outbound_billable_km),
        return_billable_km:
          deliveryForm.return_billable_km === "" ? 0 : Number(deliveryForm.return_billable_km),
        billable_km:
          deliveryForm.billable_km === "" ? 0 : Number(deliveryForm.billable_km),
        rate_per_km:
          deliveryForm.rate_per_km === "" ? 0 : Number(deliveryForm.rate_per_km),
        outbound_fee:
          deliveryForm.outbound_fee === "" ? 0 : Number(deliveryForm.outbound_fee),
        return_fee:
          deliveryForm.return_fee === "" ? 0 : Number(deliveryForm.return_fee),
        delivery_fee:
          deliveryForm.delivery_fee === "" ? 0 : Number(deliveryForm.delivery_fee),
      };

      if (hasDeliveryRow) {
        const { error: deliveryErr } = await supabase
          .from("booking_delivery")
          .update(deliveryPayload)
          .eq("booking_id", bookingId);

        if (deliveryErr) throw deliveryErr;
      } else {
        const { error: deliveryErr } = await supabase
          .from("booking_delivery")
          .insert({
            booking_id: bookingId,
            ...deliveryPayload,
          });

        if (deliveryErr) throw deliveryErr;
      }

      if (daysDelta !== 0 && row?.membership_id) {
        const currentDays = Number(row?.memberships?.days_remaining ?? 0);
        const nextDays = Math.max(0, currentDays + daysDelta);

        const { error: membershipErr } = await supabase
          .from("memberships")
          .update({ days_remaining: nextDays })
          .eq("id", row.membership_id);

        if (membershipErr) throw membershipErr;
      }

      const fresh = await loadBooking();
      setRow(fresh);

      setForm({
        status: fresh?.status || "requested",
        start_date: fresh?.start_date || "",
        end_date: fresh?.end_date || "",
        pickup_location: fresh?.pickup_location || "",
        concierge: fresh?.concierge || "",
        notes: fresh?.notes || "",
      });

      const d = fresh?.booking_delivery || null;
      setDeliveryForm({
        service_type: d?.service_type || "pickup",
        delivery_address: d?.delivery_address || "",
        free_trip_used: !!d?.free_trip_used,
        trips_used: d?.trips_used ?? 0,
        outbound_trip_used: !!d?.outbound_trip_used,
        return_trip_used: !!d?.return_trip_used,
        outbound_distance_km: d?.outbound_distance_km ?? "",
        return_distance_km: d?.return_distance_km ?? "",
        distance_km: d?.distance_km ?? "",
        included_km: d?.included_km ?? "",
        outbound_billable_km: d?.outbound_billable_km ?? "",
        return_billable_km: d?.return_billable_km ?? "",
        billable_km: d?.billable_km ?? "",
        rate_per_km: d?.rate_per_km ?? "",
        outbound_fee: d?.outbound_fee ?? "",
        return_fee: d?.return_fee ?? "",
        delivery_fee: d?.delivery_fee ?? "",
      });

      setSaveMsg("Saved.");
    } catch (e) {
      setErr(e.message || "Failed to save booking.");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    const ok = window.confirm("Delete this booking? This cannot be undone.");
    if (!ok) return;

    setBusy(true);
    setErr(null);

    try {
      const currentStatus = String(row?.status || "").toLowerCase();
      const currentDays = safeDaysBetween(row?.start_date, row?.end_date);

      if (currentStatus === "completed" && row?.membership_id) {
        const membershipDays = Number(row?.memberships?.days_remaining ?? 0);
        const restoredDays = Math.max(0, membershipDays + currentDays);

        const { error: membershipErr } = await supabase
          .from("memberships")
          .update({ days_remaining: restoredDays })
          .eq("id", row.membership_id);

        if (membershipErr) throw membershipErr;
      }

      const { error: deliveryErr } = await supabase
        .from("booking_delivery")
        .delete()
        .eq("booking_id", bookingId);

      if (deliveryErr) throw deliveryErr;

      const { error } = await supabase.from("bookings").delete().eq("id", bookingId);
      if (error) throw error;

      nav("/admin/bookings");
    } catch (e) {
      setErr(e.message || "Failed to delete booking.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="page__head">
          <div className="page__title">Booking</div>
          <div className="page__sub">Loading…</div>
        </div>
      </div>
    );
  }

  if (err && !row) {
    return (
      <div className="page">
        <div className="page__head page__head--row">
          <div>
            <div className="page__back">
              <Link className="link" to="/admin/bookings">← Bookings</Link>
            </div>
            <h1 className="page__title">Booking</h1>
            <div className="page__sub">Could not load this booking.</div>
          </div>
        </div>

        <div className="adminAuth__error" style={{ marginTop: 12 }}>
          {err}
        </div>
        <EmptyState title="Booking not found" hint="Check the URL or go back to the bookings list." />
      </div>
    );
  }

  if (!row) {
    return (
      <div className="page">
        <EmptyState title="Booking not found" hint="Go back to bookings." />
      </div>
    );
  }

  const memberName = contact ? fullNameFromContact(contact) : "—";
  const vehicleName = vehicle?.name || row.vehicle_id;

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <div className="page__back">
            <Link className="link" to="/admin/bookings">← Bookings</Link>
          </div>
          <h1 className="page__title">Booking {String(row.id).slice(0, 8)}</h1>
          <div className="page__sub">
            {form.start_date} → {form.end_date} • {vehicleName}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <Badge tone={toneForStatus(form.status)}>{String(form.status).toUpperCase()}</Badge>

          <button className="btn btn--primary" type="button" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : "Save Changes"}
          </button>

          <button className="btn btn--danger" type="button" onClick={onDelete} disabled={busy}>
            Delete
          </button>
        </div>
      </div>

      {err && <div className="adminAuth__error" style={{ marginTop: 10 }}>{err}</div>}
      {saveMsg && <div className="memMuted" style={{ marginTop: 10 }}>{saveMsg}</div>}

      <div className="grid grid--2" style={{ marginTop: 12 }}>
        <div className="stack">
          <Card title="Booking Details" subtitle="Edit the booking record">
            <div className="formGrid">
              <div className="formRow2">
                <div>
                  <div className="memLabel">Status</div>
                  <select
                    className="memSelect"
                    value={form.status}
                    onChange={(e) => set("status", e.target.value)}
                    disabled={busy}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="memLabel">Days (derived)</div>
                  <div className="memInput" style={{ display: "flex", alignItems: "center" }}>
                    {bookingDays}
                  </div>
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Start date</div>
                  <input
                    className="memInput"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => set("start_date", e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div>
                  <div className="memLabel">End date</div>
                  <input
                    className="memInput"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => set("end_date", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Pickup location</div>
                  <input
                    className="memInput"
                    value={form.pickup_location}
                    onChange={(e) => set("pickup_location", e.target.value)}
                    disabled={busy}
                    placeholder="optional"
                  />
                </div>
                <div>
                  <div className="memLabel">Concierge</div>
                  <input
                    className="memInput"
                    value={form.concierge}
                    onChange={(e) => set("concierge", e.target.value)}
                    disabled={busy}
                    placeholder="optional"
                  />
                </div>
              </div>

              <div>
                <div className="memLabel">Notes</div>
                <textarea
                  className="memInput"
                  rows={4}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  disabled={busy}
                  placeholder="optional"
                />
              </div>

              <div className="memMuted">
                Created: {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
              </div>
            </div>
          </Card>

          <Card title="Delivery / Pickup Details" subtitle="Booking delivery info">
            <div className="formGrid">
              <div className="formRow2">
                <div>
                  <div className="memLabel">Service type</div>
                  <select
                    className="memSelect"
                    value={deliveryForm.service_type}
                    onChange={(e) => setDelivery("service_type", e.target.value)}
                    disabled={busy}
                  >
                    <option value="pickup">pickup</option>
                    <option value="delivery">delivery</option>
                  </select>
                </div>

                <div>
                  <div className="memLabel">Trips used</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    value={deliveryForm.trips_used}
                    onChange={(e) => setDelivery("trips_used", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div>
                <div className="memLabel">Delivery address</div>
                <input
                  className="memInput"
                  value={deliveryForm.delivery_address}
                  onChange={(e) => setDelivery("delivery_address", e.target.value)}
                  disabled={busy}
                  placeholder="optional"
                />
              </div>

              <div className="formRow2">
                <label className="checkRow">
                  <input
                    type="checkbox"
                    checked={!!deliveryForm.free_trip_used}
                    onChange={(e) => setDelivery("free_trip_used", e.target.checked)}
                    disabled={busy}
                  />
                  <span>Free trip used</span>
                </label>

                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <label className="checkRow" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={!!deliveryForm.outbound_trip_used}
                      onChange={(e) => setDelivery("outbound_trip_used", e.target.checked)}
                      disabled={busy}
                    />
                    <span>Outbound trip</span>
                  </label>

                  <label className="checkRow" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={!!deliveryForm.return_trip_used}
                      onChange={(e) => setDelivery("return_trip_used", e.target.checked)}
                      disabled={busy}
                    />
                    <span>Return trip</span>
                  </label>
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Outbound distance (km)</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.outbound_distance_km}
                    onChange={(e) => setDelivery("outbound_distance_km", e.target.value)}
                    disabled={busy}
                  />
                </div>

                <div>
                  <div className="memLabel">Return distance (km)</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.return_distance_km}
                    onChange={(e) => setDelivery("return_distance_km", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Total distance (km)</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.distance_km}
                    onChange={(e) => setDelivery("distance_km", e.target.value)}
                    disabled={busy}
                  />
                </div>

                <div>
                  <div className="memLabel">Included km</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.included_km}
                    onChange={(e) => setDelivery("included_km", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Outbound billable km</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.outbound_billable_km}
                    onChange={(e) => setDelivery("outbound_billable_km", e.target.value)}
                    disabled={busy}
                  />
                </div>

                <div>
                  <div className="memLabel">Return billable km</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.return_billable_km}
                    onChange={(e) => setDelivery("return_billable_km", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Total billable km</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.1"
                    value={deliveryForm.billable_km}
                    onChange={(e) => setDelivery("billable_km", e.target.value)}
                    disabled={busy}
                  />
                </div>

                <div>
                  <div className="memLabel">Rate per km</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryForm.rate_per_km}
                    onChange={(e) => setDelivery("rate_per_km", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Outbound fee</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryForm.outbound_fee}
                    onChange={(e) => setDelivery("outbound_fee", e.target.value)}
                    disabled={busy}
                  />
                </div>

                <div>
                  <div className="memLabel">Return fee</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryForm.return_fee}
                    onChange={(e) => setDelivery("return_fee", e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              <div>
                <div className="memLabel">Total delivery fee</div>
                <input
                  className="memInput"
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryForm.delivery_fee}
                  onChange={(e) => setDelivery("delivery_fee", e.target.value)}
                  disabled={busy}
                />
              </div>

              <div className="memMuted">
                Current total: <b>{formatMoney(deliveryForm.delivery_fee)}</b>
              </div>
            </div>
          </Card>
        </div>

        <div className="stack">
          <Card title="Linked Records" subtitle="Quick jump to member/vehicle">
            <div className="memInfoBlock">
              <div className="memBlockTitle">Member</div>
              <div className="memMuted" style={{ marginBottom: 8 }}>
                {memberName} • {contact?.email || "—"}
              </div>
              {row.membership_id ? (
                <Link className="btn" to={`/admin/members/${row.membership_id}`}>
                  View Member
                </Link>
              ) : (
                <div className="muted">No membership linked.</div>
              )}
            </div>

            <div style={{ height: 12 }} />

            <div className="memInfoBlock">
              <div className="memBlockTitle">Vehicle</div>
              <div className="memMuted" style={{ marginBottom: 8 }}>
                {vehicleName} {vehicle?.category ? `• ${vehicle.category}` : ""}
              </div>
              {row.vehicle_id ? (
                <Link className="btn" to={`/admin/vehicles/${row.vehicle_id}`}>
                  View Vehicle
                </Link>
              ) : (
                <div className="muted">No vehicle linked.</div>
              )}
            </div>

            <div style={{ height: 12 }} />

            <div className="memInfoBlock">
              <div className="memBlockTitle">IDs</div>
              <div className="memMuted">Booking ID: {row.id}</div>
              <div className="memMuted">Membership ID: {row.membership_id || "—"}</div>
              <div className="memMuted">Vehicle ID: {row.vehicle_id || "—"}</div>
              <div className="memMuted">Created by: {row.created_by || "—"}</div>
              <div className="memMuted">
                Member days remaining: {row?.memberships?.days_remaining ?? "—"}
              </div>
            </div>

            <div style={{ height: 12 }} />

            <div className="memInfoBlock">
              <div className="memBlockTitle">Delivery Summary</div>
              <div className="memMuted">Service type: {delivery?.service_type || "pickup"}</div>
              <div className="memMuted">Delivery address: {delivery?.delivery_address || "—"}</div>
              <div className="memMuted">Free trip used: {delivery?.free_trip_used ? "Yes" : "No"}</div>
              <div className="memMuted">Trips used: {delivery?.trips_used ?? 0}</div>
              <div className="memMuted">
                Outbound fee: {formatMoney(delivery?.outbound_fee || 0)}
              </div>
              <div className="memMuted">
                Return fee: {formatMoney(delivery?.return_fee || 0)}
              </div>
              <div className="memMuted">
                Total delivery fee: {formatMoney(delivery?.delivery_fee || 0)}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}